import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import "./getStarted.css";


const FORMSPREE_ENDPOINT = "https://formspree.io/f/xdkzvppw";
const CALENDLY_BASE =
  "https://calendly.com/elliotcarlson-rain/rain-meeting-30-mins";

const EMPTY_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  role: "",
  organization: "",
  optIn: true,
};

/* Calendly's widget assets are only pulled in once someone actually
   asks for a time, so the home page doesn't pay for them on load. */
const loadCalendly = () =>
  new Promise((resolve) => {
    if (window.Calendly) return resolve();

    if (!document.querySelector("link[data-calendly]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.dataset.calendly = "true";
      document.head.appendChild(link);
    }

    if (!document.querySelector("script[data-calendly]")) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.dataset.calendly = "true";
      document.head.appendChild(script);
    }

    const poll = () => (window.Calendly ? resolve() : setTimeout(poll, 100));
    poll();
  });

const SIGNUP_URL = "https://app.raincareers.com/auth?mode=signUp";

/* Booking lives in one place — the form at the foot of the career centre
   page. ScrollToTop resolves the hash, so this lands on the form rather
   than the top of that page. */
const BOOK_URL = "/career-centers#book";

const GetStarted = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const calendlyRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          role: formData.role,
          school: formData.organization,
          newsletter: formData.optIn ? "Yes" : "No",
          source: "Home page: Ready to get started",
        }),
      });
    } catch (err) {
      // The lead capture is best-effort; never block the booking on it.
      console.error("Lead submission failed:", err);
    }

    const enc = encodeURIComponent;
    setCalendlyUrl(
      `${CALENDLY_BASE}?hide_event_type_details=1&hide_gdpr_banner=1` +
        `&name=${enc(`${formData.firstName} ${formData.lastName}`.trim())}` +
        `&email=${enc(formData.email)}` +
        `&a1=${enc(formData.organization)}` +
        `&a2=${enc("Intro Call")}`
    );
    setSubmitting(false);
  };

  useEffect(() => {
    if (!calendlyUrl) return;
    let cancelled = false;

    loadCalendly().then(() => {
      if (cancelled || !calendlyRef.current) return;
      window.Calendly.initInlineWidget({
        url: calendlyUrl,
        parentElement: calendlyRef.current,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [calendlyUrl]);

  return (
    <section className="home-cta" id="get-started">
      <div className="home-container">
        {/* A module rather than a full-bleed band. It opens as two paths —
            students sign up, career centers book time — and swaps in place
            to the booking form rather than sending anyone to another page. */}
        <div className={`home-cta-module${showForm ? " home-cta-module-form" : ""}`}>
          {showForm ? (
            <div className="home-cta-booking">
              <button
                type="button"
                className="home-cta-back"
                onClick={() => {
                  setShowForm(false);
                  setCalendlyUrl(null);
                }}
              >
                &larr; Back
              </button>

              <h2 className="home-cta-form-title">Book a walkthrough</h2>
              <p className="home-cta-form-sub">
                Thirty minutes with our founders, shaped around your campus.
              </p>

              <div className="home-cta-panel">
                {calendlyUrl ? (
                  <div ref={calendlyRef} className="home-cta-calendly-widget" />
                ) : (
                  <form className="home-cta-form" onSubmit={handleSubmit}>
                    <input
                      type="email"
                      name="email"
                      placeholder="Work Email*"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />

                    <div className="home-cta-row">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="First name*"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Last name*"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="home-cta-row">
                      <input
                        type="text"
                        name="role"
                        placeholder="Job title*"
                        value={formData.role}
                        onChange={handleChange}
                        required
                      />
                      <input
                        type="text"
                        name="organization"
                        placeholder="School or company*"
                        value={formData.organization}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <label className="home-cta-check">
                      <input
                        type="checkbox"
                        name="optIn"
                        checked={formData.optIn}
                        onChange={handleChange}
                      />
                      <span>
                        Send me new insights, resources, and career readiness
                        tools from RAIN.
                      </span>
                    </label>

                    <button
                      type="submit"
                      className="home-cta-submit"
                      disabled={submitting}
                    >
                      {submitting ? "One moment…" : "Pick a time"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          ) : (
            <>
              <h2 className="home-cta-title">
                See how RAIN works for you
              </h2>

              <p className="home-cta-sub">
                Free for job seekers. Walkthroughs for career centers.
              </p>

              <div className="home-cta-actions">
                <a className="home-cta-primary" href={SIGNUP_URL}>
                  Sign up free
                </a>

                <Link className="home-cta-secondary" to={BOOK_URL}>
                  Book a walkthrough
                  <ChevronRight size={16} strokeWidth={2} />
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default GetStarted;
